/**
 * 자금원 — 이 모델의 축. **통장 하나 = 한 행**이다.
 *
 * 설계: `docs/02-design/000-data-model.md`
 *
 * ⚠️ `kind`(자금 구분)와 `legalForm`(법적 형태)을 나눈 것이 핵심이다.
 *    자금 구분은 지금 알고, 법적 형태는 모른다 — 조합인지 자문인지 아직 확인
 *    전이다. 하나로 합치면 모르는 것 때문에 아는 것도 못 적는다.
 *
 * 자금 입구는 둘이다 (2026-09-21 확인)
 *   ① GRK 자체 투자 — 고유자금
 *   ② 외부 기업이 **찾아와** 부탁 — 위탁자금
 *   GRK 가 나가서 모집하는 구조가 아니다.
 */
export interface Vehicle {
  id: number;

  name: string;
  /** 자금 구분. 이것만은 지금 안다 */
  kind: VehicleKind;
  /**
   * 법적 형태. **모르면 null 로 둔다.**
   * 조합 명의 사업자등록증 · 통장 명의 · 규약인가 자문계약서인가로 확인한다.
   */
  legalForm: LegalForm | null;
  /** 조합 명의 사업자번호. 있으면 별도 실체라는 증거다 */
  bizNo: string | null;
  /** 통장 명의 — 누구 돈인지가 여기서 드러난다 */
  accountName: string | null;

  startedOn: string;
  /** 청산·종료 */
  endedOn: string | null;
  status: VehicleStatus;
  note: string;

  createdAt: string;

  // 재원 기본정보 · VICS · 라이프사이클 — DB v0.3. 조합만 채운다
  vehicleType?: string | null;
  vicsCode?: string | null;
  vicsCodeName?: string | null;
  formedOn?: string | null;
  investEndOn?: string | null;
  maturityOn?: string | null;
  purpose?: string | null;
  restrictions?: string[];
  paymentMethod?: 'lump_sum' | 'installment' | null;
  fiscalYearEnd?: string | null;
  auditor?: string | null;
  mgmtFeeRate?: number | null;
  mgmtFeeBase?: FeeBase | null;
  /** 투자기간이 끝난 뒤 기준 — 비면 mgmtFeeBase 그대로 (009) */
  mgmtFeeBaseAfter?: FeeBase | null;
  /** 결성 때 미리 받는 연수 — 비면 선취 없음 (009) */
  mgmtFeePrepaidYears?: number | null;
  /** 등록일 — 조합은 중기부 등록일, 고유계정은 창업기획자 등록일. 투자의무 3년의 기준 (009) */
  registeredOn?: string | null;
  carryRate?: number | null;
  hurdleRate?: number | null;
  lossOrder?: string | null;
  leadManagerEmployeeId?: number | null;
  keyPersonEmployeeIds?: number[];
}

/** 관리보수 기준 — 약정총액 · 납입총액 · 투자잔액(회수 · 상각 전 투자 원금) */
export type FeeBase = 'committed' | 'paid_in' | 'invested';
export const FEE_BASE_LABEL: Record<FeeBase, string> = { committed: '약정총액', paid_in: '납입총액', invested: '투자잔액' };

export const VEHICLE_KINDS = ['own', 'entrusted'] as const;
export type VehicleKind = (typeof VEHICLE_KINDS)[number];

export const KIND_LABEL: Record<VehicleKind, string> = {
  own: '고유자금',
  entrusted: '위탁자금',
};

export const KIND_NOTE: Record<VehicleKind, string> = {
  own: 'GRK 법인 자금',
  entrusted: '조합 · 위탁 자금 (GRK 자산 아님, GRK 수입은 보수)',
};

export const KIND_STYLE: Record<VehicleKind, string> = {
  own: 'bg-emerald-50 text-emerald-700',
  entrusted: 'bg-amber-50 text-amber-700',
};

/** 아직 확인 전이라 값이 없을 수 있다 */
export const LEGAL_FORMS = ['조합', '자문', '일임'] as const;
export type LegalForm = (typeof LEGAL_FORMS)[number];

export const LEGAL_FORM_NOTE: Record<LegalForm, string> = {
  '조합': '조합 명의 통장 · 조합 명의 투자',
  '자문': '기업 명의 그대로 투자, GRK 는 자문 수수료',
  '일임': '기업 자금을 GRK 가 운용',
};

export const VEHICLE_STATUSES = ['active', 'closed'] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

export const STATUS_LABEL: Record<VehicleStatus, string> = {
  active: '운용 중',
  closed: '종료',
};

/**
 * 참여자 — 이 자금원에 돈을 댄 쪽. **GRK 자신도 한 행**으로 들어간다.
 *
 * ⚠️ 실제 납입액은 여기 저장하지 않는다. 원장에 `contribution` 으로 쌓이고
 *    합계로 계산한다. 두 곳에 두면 반드시 어긋난다.
 */
export interface Participant {
  id: number;
  vehicleId: number;
  name: string;
  /** 운용하는 쪽인가 */
  isManager: boolean;
  /** 약정액(원). 모르면 null */
  committedAmount: number | null;
  /** 분배 비율. 비어 있으면 납입 비율로 계산한다 */
  shareRatio: number | null;
  note: string;
}

export interface VehicleForm {
  name: string;
  kind: VehicleKind;
  legalForm: LegalForm | '';
  bizNo: string;
  accountName: string;
  startedOn: string;
  note: string;
  participants: ParticipantForm[];
}

export interface ParticipantForm {
  id: number;
  name: string;
  isManager: boolean;
  committedAmount: number | null;
}

export const won = (v: number | null): string =>
  v === null ? '—' : `₩${v.toLocaleString()}`;

/** 억 단위로 줄여 쓴다. 목록에서만 */
export const shortWon = (v: number | null): string => {
  if (v === null || v === 0) return '—';
  // 10억 미만은 소수 둘째 자리까지 — toFixed(1) 은 3.55억을 「3.5억」으로 보였다(2026-11-02 1호 약정)
  if (v >= 100_000_000) {
    const e = v / 100_000_000;
    return `${Number(e.toFixed(e < 10 ? 2 : 1)).toLocaleString()}억`;
  }
  return `${Math.round(v / 10_000).toLocaleString()}만`;
};
